import React, { useEffect, useRef, useState } from 'react'
import Banner from './Banner';
import { ReactComponent as SuccessIcon } from '../../assets/icons/success.svg';
import { ReactComponent as LeftArrowIcon } from '../../assets/plus_icon/leftArrow.svg';
import * as animationData from '../../assets/success_lottie.json'
import Lottie from 'react-lottie';
import { toast } from 'react-toastify';
import InputSelect from '../../components/InputGroup/InputSelect';
import { getMasterOtpReasons } from '../../services/private/site-visit.service';

const numberOfDigits = 6
const SiteVisitVerification = ({
    setChannelPartner,
    otpVerifield,
    handleVerifyOTP,
    countDown,
    disableResend,
    mobile, otpError,
    masterOtpError,
    siteHeadOtpError,
    handleSendOTP,
    name,
    email,
    handleSubmitCPVisit,
    isCPVisit, isSubmitting, isRevisit, setShowCustomerRevisitModal,
    isChangeTagging,
    coverImage,
    projectName }) => {
    const [otpVal, setOTPVal] = useState([]);
    const inputRef = useRef(null);
    const [otp, setOtp] = useState(new Array(numberOfDigits).fill(""));
    const otpBoxReference = useRef([]);

    const [siteHeadOtp, setSiteHeadOtp] = useState(new Array(numberOfDigits).fill(""));
    const siteHeadOtpBoxReference = useRef([]);

    // Master OTP state
    const [isMasterOtp, setIsMasterOtp] = useState(false);
    const [masterOtp, setMasterOtp] = useState(new Array(numberOfDigits).fill(""));
    const masterOtpBoxReference = useRef([]);
    const [masterOtpReasons, setMasterOtpReasons] = useState([]);
    const [selectedReason, setSelectedReason] = useState(null);
    const [isReasonDropdownOpen, setIsReasonDropdownOpen] = useState(false);
    const reasonFieldRef = useRef(null);
    const [masterOtpReasonText, setMasterOtpReasonText] = useState('');
    const [reasonError, setReasonError] = useState('');
    const [reasonTextError, setReasonTextError] = useState('');
    const [loadingReasons, setLoadingReasons] = useState(false);

    useEffect(() => {
        let isMounted = true;
        if (isMasterOtp && masterOtpReasons.length === 0) {
            setLoadingReasons(true);
            getMasterOtpReasons()
                .then(res => {
                    if (!isMounted) return;
                    if (res?.data?.status === 200 && Array.isArray(res?.data?.data)) {
                        setMasterOtpReasons(res.data.data);
                    }
                })
                .catch(err => {
                    if (!isMounted) return;
                    console.error("Error fetching master OTP reasons:", err);
                    toast.error(err?.response?.data?.message || "Failed to load master OTP reasons");
                })
                .finally(() => {
                    if (isMounted) setLoadingReasons(false);
                });
        }
        return () => {
            isMounted = false;
        };
    }, [isMasterOtp]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (reasonFieldRef.current && !reasonFieldRef.current.contains(event.target)) {
                setIsReasonDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (isChangeTagging && isMasterOtp) {
            setIsMasterOtp(false);
            setMasterOtp(new Array(numberOfDigits).fill(""));
            setSelectedReason(null);
            setMasterOtpReasonText('');
            setReasonError('');
            setReasonTextError('');
            setIsReasonDropdownOpen(false);
        }
    }, [isChangeTagging, isMasterOtp]);

    function handleMasterOtpToggle(checked) {
        setIsMasterOtp(checked);
        setReasonError('');
        setReasonTextError('');
        setIsReasonDropdownOpen(false);
        if (!checked) {
            setMasterOtp(new Array(numberOfDigits).fill(""));
            setSelectedReason(null);
            setMasterOtpReasonText('');
        }
    }

    function handleChange(value, index) {
        const cleanDigits = value.replace(/[^0-9]/g, '');
        let newArr = [...otp];
        if (cleanDigits.length > 1) {
            // If user pastes or browser autocompletes multiple digits
            const digits = cleanDigits.split('').slice(0, numberOfDigits - index);
            digits.forEach((d, i) => {
                newArr[index + i] = d;
            });
            setOtp(newArr);
            const nextIdx = Math.min(index + digits.length, numberOfDigits - 1);
            if (otpBoxReference.current[nextIdx]) {
                otpBoxReference.current[nextIdx].focus();
            }
        } else {
            const singleVal = cleanDigits.slice(-1);
            newArr[index] = singleVal;
            setOtp(newArr);
            if (singleVal && index < numberOfDigits - 1 && otpBoxReference.current[index + 1]) {
                otpBoxReference.current[index + 1].focus();
            }
        }
    }

    function handleBackspaceAndEnter(e, index) {
        if (e.key === "Backspace" && !e.target.value && index > 0) {
            otpBoxReference.current[index - 1].focus()
        }
        if (e.key === "Enter" && e.target.value && index < numberOfDigits - 1) {
            otpBoxReference.current[index + 1].focus()
        }
    }

    function handleMasterOtpChange(value, index) {
        const cleanDigits = value.replace(/[^0-9]/g, '');
        let newArr = [...masterOtp];
        if (cleanDigits.length > 1) {
            const digits = cleanDigits.split('').slice(0, numberOfDigits - index);
            digits.forEach((d, i) => {
                newArr[index + i] = d;
            });
            setMasterOtp(newArr);
            const nextIdx = Math.min(index + digits.length, numberOfDigits - 1);
            if (masterOtpBoxReference.current[nextIdx]) {
                masterOtpBoxReference.current[nextIdx].focus();
            }
        } else {
            const singleVal = cleanDigits.slice(-1);
            newArr[index] = singleVal;
            setMasterOtp(newArr);
            if (singleVal && index < numberOfDigits - 1 && masterOtpBoxReference.current[index + 1]) {
                masterOtpBoxReference.current[index + 1].focus();
            }
        }
    }

    function handleMasterOtpBackspaceAndEnter(e, index) {
        if (e.key === "Backspace" && !e.target.value && index > 0) {
            masterOtpBoxReference.current[index - 1].focus()
        }
        if (e.key === "Enter" && e.target.value && index < numberOfDigits - 1) {
            masterOtpBoxReference.current[index + 1].focus()
        }
    }

    function handleSiteHeadChange(value, index) {
        const cleanDigits = value.replace(/[^0-9]/g, '');
        let newArr = [...siteHeadOtp];
        if (cleanDigits.length > 1) {
            const digits = cleanDigits.split('').slice(0, numberOfDigits - index);
            digits.forEach((d, i) => {
                newArr[index + i] = d;
            });
            setSiteHeadOtp(newArr);
            const nextIdx = Math.min(index + digits.length, numberOfDigits - 1);
            if (siteHeadOtpBoxReference.current[nextIdx]) {
                siteHeadOtpBoxReference.current[nextIdx].focus();
            }
        } else {
            const singleVal = cleanDigits.slice(-1);
            newArr[index] = singleVal;
            setSiteHeadOtp(newArr);
            if (singleVal && index < numberOfDigits - 1 && siteHeadOtpBoxReference.current[index + 1]) {
                siteHeadOtpBoxReference.current[index + 1].focus();
            }
        }
    }

    function handleSiteHeadBackspaceAndEnter(e, index) {
        if (e.key === "Backspace" && !e.target.value && index > 0) {
            siteHeadOtpBoxReference.current[index - 1].focus()
        }
        if (e.key === "Enter" && e.target.value && index < numberOfDigits - 1) {
            siteHeadOtpBoxReference.current[index + 1].focus()
        }
    }

    useEffect(() => {
        if (otpError) {
            setOtp(new Array(numberOfDigits).fill(""));
            if (otpBoxReference.current[0]) {
                otpBoxReference.current[0].focus();
            }
        }
    }, [otpError]);

    useEffect(() => {
        if (masterOtpError) {
            setMasterOtp(new Array(numberOfDigits).fill(""));
            if (masterOtpBoxReference.current[0]) {
                masterOtpBoxReference.current[0].focus();
            }
        }
    }, [masterOtpError]);

    useEffect(() => {
        if (siteHeadOtpError) {
            setSiteHeadOtp(new Array(numberOfDigits).fill(""));
            if (siteHeadOtpBoxReference.current[0]) {
                siteHeadOtpBoxReference.current[0].focus();
            }
        }
    }, [siteHeadOtpError]);

    const handleFormNext = () => {
        const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.hostname.includes('dev') || process.env.NODE_ENV === 'development';
        
        if (isMasterOtp) {
            // Validate Reason
            if (!selectedReason || !selectedReason.value) {
                setReasonError('Please select a reason for using Master OTP');
                toast.error('Please select a reason for using Master OTP', { toastId: 'master-otp-reason-err' });
                return;
            }
            setReasonError('');

            // Validate Reason Text if 'other'
            if (selectedReason.value === 'other') {
                const trimmedText = masterOtpReasonText.trim();
                if (!trimmedText) {
                    setReasonTextError('Reason description is required');
                    toast.error('Please enter reason description', { toastId: 'master-otp-reason-text-err' });
                    return;
                }
                if (trimmedText.length < 5) {
                    setReasonTextError('Reason text must be at least 5 characters');
                    toast.error('Reason text must be at least 5 characters', { toastId: 'master-otp-reason-min-err' });
                    return;
                }
                if (trimmedText.length > 250) {
                    setReasonTextError('Reason text must not exceed 250 characters');
                    toast.error('Reason text must not exceed 250 characters', { toastId: 'master-otp-reason-max-err' });
                    return;
                }
            }
            setReasonTextError('');

            const masterOtpStr = masterOtp.join('');
            const isTestingMasterOtp = isLocalDev && (masterOtpStr === '0000' || masterOtpStr === '000000');
            if (!isTestingMasterOtp && masterOtpStr.length < 6) {
                toast.error('Please enter a valid 6-digit Master OTP.', { toastId: 'master-otp-len-err' });
                return;
            }

            if (isChangeTagging) {
                const siteHeadOtpStr = siteHeadOtp.join('');
                const isTestingSiteHeadOtp = isLocalDev && (siteHeadOtpStr === '0000' || siteHeadOtpStr === '000000');
                if (!isTestingSiteHeadOtp && siteHeadOtpStr.length < 6) {
                    toast.error('Please enter a valid 6-digit Site Head OTP.', { toastId: 'sitehead-otp-len-err' });
                    return;
                }
            }

            if (isCPVisit) {
                handleSubmitCPVisit(masterOtpStr, {
                    is_master_otp: true,
                    master_otp_reason: selectedReason.value,
                    master_otp_reason_text: selectedReason.value === 'other' ? masterOtpReasonText.trim() : null
                });
            } else {
                handleVerifyOTP(masterOtpStr, isChangeTagging ? siteHeadOtp.join('') : null, {
                    isMasterOtp: true,
                    masterOtpReason: selectedReason.value,
                    masterOtpReasonText: selectedReason.value === 'other' ? masterOtpReasonText.trim() : null
                });
            }
            return;
        }

        // Normal Customer OTP flow
        const otpStr = otp.join('');
        const isTestingOtp = isLocalDev && (otpStr === '0000' || otpStr === '000000');

        if (!isTestingOtp && otpStr.length < 6) {
            toast.error('Please enter a valid 6-digit OTP.', { toastId: 'otp-len-err' });
            return;
        }

        if (isChangeTagging) {
            const siteHeadOtpStr = siteHeadOtp.join('');
            const isTestingSiteHeadOtp = isLocalDev && (siteHeadOtpStr === '0000' || siteHeadOtpStr === '000000');
            if (!isTestingSiteHeadOtp && siteHeadOtpStr.length < 6) {
                toast.error('Please enter a valid 6-digit Site Head OTP.', { toastId: 'sitehead-otp-len-err' });
                return;
            }
        }

        if (isCPVisit) {
            handleSubmitCPVisit(otpStr, {
                is_master_otp: false
            });
        } else {
            handleVerifyOTP(otpStr, isChangeTagging ? siteHeadOtp.join('') : null);
        }
    };


    // const inputfocus = (e) => {
    //     if (e.key === "Delete" || e.key === "Backspace") {
    //         let newVal = [...otpVal]
    //         newVal[e.target.tabIndex] = ''
    //         setOTPVal(newVal)
    //         setOTP(newVal)
    //         const previousElementSibling =
    //             e.target.previousElementSibling;

    //         if (previousElementSibling) {
    //             previousElementSibling.focus();
    //         }
    //     }
    //     else {
    //         console.log(e.target.value, 'valuess')
    //         const nextElementSibling = e.target.nextElementSibling;
    //         let newVal = [...otpVal]
    //         newVal[e.target.tabIndex] = e.target.value
    //         setOTPVal(newVal)
    //         setOTP(newVal);
    //         if (nextElementSibling) {
    //             nextElementSibling.focus();
    //         }

    //     }
    // }

    return (
        <div className=' mx-auto overflow-auto '>
            <div className='!w-full broker-info'>
                <Banner coverImage={coverImage} projectName={projectName} />
            </div>
            {!otpVerifield && <>
                <div className='flex items-center !my-3 sm:!my-6 !py-6 sm:!py-12 bg-white rounded-xl'>
                    {/* <span className='w-[145px] border-t-2 border-[#696969]'></span> */}
                    <p className='flex items-center mb-0 mx-4 sm:!mx-10'>
                        <span className='cursor-pointer' onClick={() => {
                            if (isRevisit) {
                                setChannelPartner('1')
                                setShowCustomerRevisitModal(true)
                            } else {
                                setChannelPartner('5')
                            }
                        }}>
                            <LeftArrowIcon className='stroke-black800 rotate-180' />
                        </span>
                        <span className='text-lg sm:text-2xl font-medium ml-4 sm:!ml-12'>Verify your Site Visit</span>
                        {/* <span className='text-xl !mt-4'>Powered By Propacity</span> */}
                    </p>
                    {/* <span className='w-[145px] border-t-2 border-[#696969]'></span> */}
                </div>
                <div className='bg-white rounded-xl  p-4 sm:!p-10 space-y-6'>
                    {/* Master OTP Option - only visible when Site Head OTP is NOT required */}
                    {!isChangeTagging && (
                        <div className='flex items-center space-x-3 mb-2'>
                            <input
                                type='checkbox'
                                id='useMasterOtpCheckbox'
                                checked={isMasterOtp}
                                onChange={(e) => handleMasterOtpToggle(e.target.checked)}
                                className='w-5 h-5 rounded border-gray-300 text-blue-900 focus:ring-blue-900 cursor-pointer'
                            />
                            <label htmlFor='useMasterOtpCheckbox' className='text-sm sm:text-base font-medium text-gray-800 cursor-pointer select-none mb-0'>
                                Use Master OTP
                            </label>
                        </div>
                    )}

                    {!isMasterOtp && (
                        <>
                            <h3 className='text-lg sm:text-xl'>{isChangeTagging ? 'Customer OTP' : 'Site Visit Code'}</h3>
                            <p className='text-sm sm:text-lg text-black700'>
                                {isChangeTagging ? 'OTP' : 'SV Code'} sent to <span className='text-textblack font-medium'>{name}</span> (<span className='text-textblack font-medium'>{mobile}</span>) {email ? 'and' : ''} <span className='text-textblack font-medium'>{email}</span>
                            </p>
                            <div className='flex !gap-4'>
                                {otp.map((digit, index) => (
                                    <input key={index} value={digit} maxLength={1} type='tel' inputMode='numeric'
                                        onChange={(e) => handleChange(e.target.value, index)}
                                        onKeyUp={(e) => handleBackspaceAndEnter(e, index)}
                                        ref={(reference) => (otpBoxReference.current[index] = reference)}
                                        className='w-[33px] h-[33px] sm:w-[80px] sm:h-[80px] font-semibold rounded-lg  bg-[#EAECF0] focus:!border-blue-900 outline-0 shadow-0 text-center focus:text-blue-900 text-textblack text-sm sm:text-2xl'
                                    />
                                ))}
                            </div>
                            {otpError && <div className="input-feedback sm:!text-sm text-xs text-red !mt-3 ml-2">Incorrect OTP, Please Retry.</div>}
                        </>
                    )}

                    {isMasterOtp && (
                        <div className='space-y-4 !mt-4 max-w-xl'>
                            <div className="flex flex-col font-medium relative w-full" ref={reasonFieldRef}>
                                <label className="sm:!text-lg text-base !mb-2">
                                    Select Master OTP Reason <sup className="text-red-500">*</sup>
                                </label>
                                <div className="relative w-full">
                                    <input
                                        type="text"
                                        readOnly
                                        className={`border rounded-xl focus:outline-none focus:border-[#0062ff] text-input sm:!text-lg text-sm !py-2 !px-3 sm:!py-3 sm:!px-4 w-full pr-9 font-normal text-gray-800 cursor-pointer bg-white ${
                                            reasonError ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                        placeholder={loadingReasons ? "Loading reasons..." : "Select reason"}
                                        value={selectedReason?.label || ''}
                                        onClick={() => {
                                            if (!loadingReasons) {
                                                setIsReasonDropdownOpen(!isReasonDropdownOpen);
                                            }
                                        }}
                                    />
                                    <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none text-xs">
                                        &#9662;
                                    </span>

                                    {isReasonDropdownOpen && (
                                        <div className="absolute left-0 right-0 top-[calc(100%+4px)] border border-gray-200 rounded-xl shadow-xl bg-white z-[999] overflow-hidden">
                                            <div className="max-h-[170px] overflow-y-auto divide-y divide-gray-100">
                                                {masterOtpReasons && masterOtpReasons.length > 0 ? (
                                                    masterOtpReasons.map((item, idx) => (
                                                        <div
                                                            key={item.value || idx}
                                                            className={`py-3 px-4 text-sm sm:text-base cursor-pointer hover:bg-gray-50 transition-colors flex items-center justify-between ${
                                                                selectedReason?.value === item.value ? 'bg-blue-50/70 text-blue-900 font-semibold' : 'text-gray-700'
                                                            }`}
                                                            onClick={() => {
                                                                setSelectedReason(item);
                                                                setIsReasonDropdownOpen(false);
                                                                setReasonError('');
                                                                if (item?.value !== 'other') {
                                                                    setMasterOtpReasonText('');
                                                                    setReasonTextError('');
                                                                }
                                                            }}
                                                        >
                                                            <span>{item.label}</span>
                                                            {selectedReason?.value === item.value && (
                                                                <span className="text-blue-600 text-xs font-bold">&#10003;</span>
                                                            )}
                                                        </div>
                                                    ))
                                                ) : !loadingReasons ? (
                                                    <div className="py-3 px-3 text-sm text-gray-400 text-center">
                                                        No reasons available
                                                    </div>
                                                ) : (
                                                    <div className="py-3 px-3 text-sm text-gray-400 text-center">
                                                        Loading reasons...
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                {reasonError && <div className="text-red sm:!text-sm text-xs font-medium !mt-2 !ml-0">{reasonError}</div>}
                            </div>

                            {selectedReason?.value === 'other' && (
                                <div className='flex flex-col font-medium relative w-full'>
                                    <div className='flex justify-between items-center !mb-2'>
                                        <label className='sm:!text-lg text-base mb-0'>
                                            Reason Description <sup className="text-red-500">*</sup>
                                        </label>
                                        <span className={`text-xs sm:text-sm font-normal ${masterOtpReasonText.length > 250 ? 'text-red font-semibold' : 'text-gray-500'}`}>
                                            {masterOtpReasonText.length}/250 (min 5)
                                        </span>
                                    </div>
                                    <textarea
                                        value={masterOtpReasonText}
                                        maxLength={250}
                                        rows={3}
                                        placeholder="Please specify the reason (5-250 characters)"
                                        onChange={(e) => {
                                            setMasterOtpReasonText(e.target.value);
                                            if (e.target.value.trim().length >= 5 && e.target.value.trim().length <= 250) {
                                                setReasonTextError('');
                                            }
                                        }}
                                        className={`w-full border rounded-xl focus:outline-none focus:border-[#0062ff] text-input sm:!text-lg text-sm !py-2 !px-3 sm:!py-3 sm:!px-4 font-normal text-gray-800 transition-colors ${
                                            reasonTextError ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                    />
                                    {reasonTextError && <div className="text-red sm:!text-sm text-xs font-medium !mt-2 !ml-0">{reasonTextError}</div>}
                                </div>
                            )}

                            <div className='!mt-6 space-y-4'>
                                <h3 className='text-lg sm:text-xl'>Master OTP</h3>
                                <p className='text-sm sm:text-lg text-black700'>
                                    Enter the 6-digit Master OTP
                                </p>
                                <div className='flex !gap-4'>
                                    {masterOtp.map((digit, index) => (
                                        <input key={index} value={digit} maxLength={1} type='tel' inputMode='numeric'
                                            onChange={(e) => handleMasterOtpChange(e.target.value, index)}
                                            onKeyUp={(e) => handleMasterOtpBackspaceAndEnter(e, index)}
                                            ref={(reference) => (masterOtpBoxReference.current[index] = reference)}
                                            className='w-[33px] h-[33px] sm:w-[80px] sm:h-[80px] font-semibold rounded-lg  bg-[#EAECF0] focus:!border-blue-900 outline-0 shadow-0 text-center focus:text-blue-900 text-textblack text-sm sm:text-2xl'
                                        />
                                    ))}
                                </div>
                                {masterOtpError && <div className="input-feedback sm:!text-sm text-xs text-red !mt-3 ml-2">Incorrect OTP, Please Retry.</div>}
                            </div>
                        </div>
                    )}
 
                    {isChangeTagging && (
                        <div className='!mt-8 space-y-4'>
                            <h3 className='text-lg sm:text-xl'>Site Head OTP</h3>
                            <p className='text-sm sm:text-lg text-black700'>
                                Enter the OTP received by the assigned Site Head
                            </p>
                            <div className='flex !gap-4'>
                                {siteHeadOtp.map((digit, index) => (
                                    <input key={index} value={digit} maxLength={1} type='tel' inputMode='numeric'
                                        onChange={(e) => handleSiteHeadChange(e.target.value, index)}
                                        onKeyUp={(e) => handleSiteHeadBackspaceAndEnter(e, index)}
                                        ref={(reference) => (siteHeadOtpBoxReference.current[index] = reference)}
                                        className='w-[33px] h-[33px] sm:w-[80px] sm:h-[80px] font-semibold rounded-lg  bg-[#EAECF0] focus:!border-blue-900 outline-0 shadow-0 text-center focus:text-blue-900 text-textblack text-sm sm:text-2xl'
                                    />
                                ))}
                            </div>
                            {siteHeadOtpError && <div className="input-feedback sm:!text-sm text-xs text-red !mt-3 ml-2">Incorrect Site Head OTP, Please Retry.</div>}
                        </div>
                    )}

                    {!isMasterOtp && (
                        <div className=' text-sm sm:text-xl !mt-10'>
                            Haven’t Received the Code ?
                            <button
                                className={`text-[#3B37F5] border-0 font-medium leading-5 ml-1 disabled:bg-transparent disabled:hover:bg-transparent disabled:cursor-not-allowed disabled:text-grey-200`} disabled={disableResend} onClick={() => {
                                    handleSendOTP();
                                }}> {countDown > 0 ? `Resend Code (${countDown})` : 'Send Again'}
                            </button>
                        </div>
                    )}
                    <div className='w-full sm:space-x-4 sm:space-y-0 space-y-4 space-y-reverse flex flex-col-reverse sm:flex-row !mt-6 sm:!mt-20' >
                        <button
                            className='w-full sm:w-[1/2] text-base sm:!text-lg border rounded-xl !py-2 sm:!py-3.5'
                            onClick={() => {
 
                                if (isRevisit) {
                                    setChannelPartner('1')
                                    setShowCustomerRevisitModal(true)
                                } else {
                                    setChannelPartner('5')
                                }
                             }}
                        >
                            Back
                        </button>
                        <button
                            className='w-full sm:w-[1/2] text-base sm:!text-lg rounded-xl !py-2 sm:!py-3.5 bg-blue-900 text-white'
                            disabled={isSubmitting}
                            onClick={handleFormNext}
                        >
                            Next
                        </button>
                    </div>
                </div>
            </>}
            {
                otpVerifield && <div className='flex items-center flex-col justify-center !my-3 sm:!my-6 !py-6 sm:!py-10 bg-white rounded-xl sm:h-[52vh]'>
                    {/* <SuccessIcon className='' /> */}
                    <div className='h-[100px] w-[100px] sm:hidden'>

                        <Lottie
                            height={100}
                            width={100}
                            options={
                                { animationData: animationData }
                            }
                        />
                    </div>
                    <div className=' hidden sm:block sm:h-[200px] sm:w-[200px] md:hidden '>

                        <Lottie
                            height={200}
                            width={200}

                            options={
                                { animationData: animationData }
                            }
                        />
                    </div>
                    <div className=' hidden md:block md:h-[150px] md:w-[200px]'>

                        <Lottie
                            height={200}
                            width={200}

                            options={
                                { animationData: animationData }
                            }
                        />
                    </div>
                    {/* <input accept=”image/*” id=”icon-button-file” type=”file” capture=”environment” /> */}

                    <h3 className='!mt-9 xl:mt-6 text-xl sm:text-3xl font-semibold'>Successfully Registered !</h3>
                    <p className='text-lg text-center sm:text-xl !mt-4 !px-4'>Your Site Visit have been completed Successfully.</p>

                    <button className='w-[95%] sm:w-1/2 !mt-10 xl:!mt-2 text-lg sm:!text-xl font-semibold rounded-xl sm:rounded-lg py-2 sm:!py-3.5 bg-blue-900 text-white' onClick={() => {
                        window.location.reload()
                    }}>Register New Customer</button>

                </div>
            }
        </div >
    )
}

export default SiteVisitVerification